import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { AuthHttpInterceptor, AuthService } from '@auth0/auth0-angular';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  Observable,
  catchError,
  of,
  switchMap,
  map,
  take,
  throwError,
  timeout,
} from 'rxjs';
import { isInMsalSilentIframe } from '@ups/xplat/web/utils';
import { AuthFacade } from './auth-facade';
import { ENTRA_LOGIN_CONFIG } from './dual-login/dual-login.ui-service';
import {
  TOKEN_ACQUIRE_BOUND_MS,
  TokenAcquireError,
  boundTokenAcquire,
  classifyAuth0AcquireFailure,
} from './token-acquire-bound';

/**
 * Marks a request already retried once after a silent token refresh so a
 * session that cannot be recovered can never spin an infinite
 * 401 -> refresh -> 401 loop. Same guard the mobile Auth0 interceptor uses.
 */
const AUTH_RETRY_HEADER = 'x-ups-auth-retry';

/**
 * MSAL reports "there is no signed-in account at all" as this error code.
 * It is categorically different from a refresh that failed: no retry, no
 * 401 round trip and no amount of waiting can produce a token, because
 * there is no session to renew. Matched on the code rather than the
 * message so a wording change upstream cannot silently un-match it.
 */
function isNoAccountError(err: unknown): boolean {
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  return code === 'no_account_error';
}

/**
 * Browser-side silent-renewal failures that, like an interaction-required
 * error, can ONLY be recovered by an interactive redirect — MSAL raises them
 * as `BrowserAuthError`, NOT `InteractionRequiredAuthError`, so they slip past
 * an interaction-required-only match and were the reason an expired session
 * proceeded unauthenticated instead of recovering (observed live: a session
 * with no cached refresh token fell into the hidden-iframe renewal path and
 * threw `block_iframe_reload`, then `timed_out`). All describe "silent renewal
 * cannot run or produced no token", never a transient network blip. Codes are
 * the msal-browser v5 spellings (verified against the installed package):
 *  - `block_iframe_reload` — the redirect URI is the app itself, so the hidden
 *                            silent-renewal iframe would reload the whole app;
 *                            MSAL blocks it. Hit when no refresh token is cached
 *                            but an account remains, so renewal falls back to
 *                            the iframe. (Addressed at the source by the
 *                            bootstrap guard in each Entra app's main.ts.)
 *  - `timed_out`           — the silent iframe never completed: the identity
 *                            provider needed interaction and tried to navigate
 *                            the top window, which the sandboxed silent iframe
 *                            blocks, so MSAL times out. (msal-browser v5; the
 *                            older `monitor_window_timeout` no longer exists.)
 *  - `no_tokens_found`     — an account is present but no usable token is
 *                            cached and none could be obtained silently.
 */
const INTERACTIVE_RECOVERABLE_BROWSER_CODES = new Set([
  'block_iframe_reload',
  'timed_out',
  'no_tokens_found',
]);

/**
 * True when a silent token acquisition failed in a way that ONLY an
 * interactive redirect can recover — the user must sign in interactively.
 * Covers two MSAL error families: the canonical interaction-required set
 * ({@link InteractionRequiredAuthError} / `interaction_required` /
 * `login_required` / `consent_required` — expired/revoked refresh token,
 * lapsed SSO, new consent or conditional-access) AND the browser silent-
 * renewal failures in {@link INTERACTIVE_RECOVERABLE_BROWSER_CODES}. In every
 * case, waiting or retrying silently can NEVER produce a token. Matched on the
 * error type and on error codes so a wording change upstream cannot silently
 * un-match it. Deliberately does NOT match `no_account_error` (no session to
 * recover — handled separately) or transient/network failures (a later call
 * succeeds, so a jarring full-page redirect would be wrong).
 */
function needsInteractiveRecovery(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) {
    return true;
  }
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  return (
    code === 'interaction_required' ||
    code === 'login_required' ||
    code === 'consent_required' ||
    (typeof code === 'string' &&
      INTERACTIVE_RECOVERABLE_BROWSER_CODES.has(code))
  );
}

/**
 * Issuer-aware implementation of the HTTP_AUTH_INTERCEPTOR seam (#29709).
 * MyHttpClientFactory takes its auth interceptor by injection token, so
 * binding this class routes every factory-built client by session issuer:
 * Entra sessions get a Bearer token from {@link AuthFacade} (MSAL silent
 * refresh under the hood); Auth0 sessions DELEGATE to the stock Auth0
 * AuthHttpInterceptor so the existing allowedList and refresh semantics
 * stay byte-identical for every pre-existing user.
 *
 * Token-failure handling is not one-size-fits-all: a transient refresh
 * failure proceeds unauthenticated (the API's 401 is the re-login signal),
 * `no_account_error` fails loudly without sending (#30531 — proceeding
 * flattened downstream into a misleading empty-permission set), and a
 * session that can no longer be renewed silently
 * (`InteractionRequiredAuthError` or the browser silent-renewal codes)
 * triggers an interactive redirect because no silent retry can ever recover
 * it (#31904).
 *
 * **Both branches recover from an expired token (#31536 Auth0, #31749/#31904
 * Entra).** #31536 gave the Auth0 branch a 401 -> forced-refresh ->
 * retry-once; a dual-issuer app on an Entra session (web-software-security's
 * standup board) left open past the MSAL token lifetime 401'd with no
 * recovery, so the Entra branch now mirrors it: a 401 after a token was
 * attached forces one non-cached refresh (`AuthFacade.getAccessToken(true)`)
 * + replay, escalating to an interactive redirect if that refresh reports the
 * session is dead. See {@link interceptEntra} for the full decision table.
 */
@Injectable()
export class IssuerAwareAuthInterceptor implements HttpInterceptor {
  private readonly facade = inject(AuthFacade);
  private readonly injector = inject(Injector);
  // Same injected config EntraAuthenticationService reads (default factory:
  // environment.entraId), so the interceptor's own MSAL calls request the exact
  // scopes the primary token flow does — a single source, provably no drift.
  private readonly entraConfig = inject(ENTRA_LOGIN_CONFIG);

  /**
   * Latches interactive recovery to at most ONE attempt per page load. A
   * single expired session typically 401s several concurrent background calls
   * (permission load, userProfile, feature checks) in the same tick; without
   * this each would call `acquireTokenRedirect`, and MSAL rejects the 2nd..Nth
   * with `interaction_in_progress`. The first request wins and navigates the
   * document to Microsoft; the rest abort quietly. NOT reset on a failed
   * launch: if the redirect cannot start (the block_iframe_reload config issue,
   * or interaction already in progress) retrying cannot help and would spin a
   * console-flooding loop. A full page load starts fresh with the latch clear.
   */
  private redirectInFlight = false;

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.facade.issuer !== 'entra') {
      return this.interceptAuth0(req, next);
    }
    return this.interceptEntra(req, next);
  }

  /**
   * Entra path. Three distinct token outcomes, three answers (#31904 — Entra
   * parity with the Auth0 branch's #31536 recovery; the recovery half was the
   * gap that stranded an expired Entra session in an unbreakable 401 loop with
   * no route back to Microsoft — the symptom that motivated this):
   *
   * 1. **`no_account_error`** — no signed-in account at all. Fail loudly and
   *    do NOT send the request (see {@link isNoAccountError}); an anonymous
   *    call flattens downstream into a misleading empty-permissions state.
   * 2. **Silent renewal cannot produce a token** — a real session that can no
   *    longer be renewed silently: `InteractionRequiredAuthError` (expired/
   *    revoked refresh token, lapsed SSO, new consent/conditional-access) OR a
   *    browser silent-renewal failure ({@link needsInteractiveRecovery}, e.g.
   *    `block_iframe_reload` when no refresh token is cached). Silent retries
   *    can never succeed, so the ONLY recovery is an interactive redirect;
   *    trigger it and abort the request (the page is about to navigate away).
   *    This is the fix for "the app can't recover when the Entra login expires".
   * 3. **Any other silent failure** — treated as transient: proceed
   *    unauthenticated so the API's 401 remains the re-login signal, exactly
   *    as before.
   *
   * Once a token IS attached, a 401 from the API is still recoverable: force
   * a non-cached silent refresh and replay once ({@link forceRefreshEntraAndRetry}),
   * mirroring the Auth0 branch's overnight-token recovery.
   */
  private interceptEntra(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.facade.getAccessToken().pipe(
      // #32087: MSAL bounds its iframe renewal but not its token-endpoint
      // fetch, so cap the whole acquire. A timeout is treated as TRANSIENT
      // (case 3 below): proceed unauthenticated and let the API's 401 drive
      // the bounded forced refresh, so the request can never hang unsent.
      timeout({
        first: TOKEN_ACQUIRE_BOUND_MS,
        with: () => throwError(() => new TokenAcquireError('entra', 'timeout')),
      }),
      map((token) =>
        req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      ),
      catchError((err) => {
        if (isNoAccountError(err)) {
          console.error(
            '[auth] no signed-in Entra account; this session cannot be authenticated. ' +
              'Re-authentication is required. Request NOT sent unauthenticated.',
            err
          );
          return throwError(
            () =>
              new Error(
                'No signed-in Entra account: the request was not sent. Sign in again.'
              )
          );
        }
        if (needsInteractiveRecovery(err)) {
          console.warn(
            '[auth] Entra session can no longer be renewed silently; ' +
              'starting interactive re-authentication.',
            err
          );
          this.triggerEntraRedirect();
          return throwError(
            () => new Error('Entra session expired; redirecting to sign in.')
          );
        }
        console.warn(
          err instanceof TokenAcquireError
            ? `[auth] silent Entra token acquisition did not settle within ${TOKEN_ACQUIRE_BOUND_MS}ms; request proceeds unauthenticated`
            : '[auth] silent Entra token acquisition failed; request proceeds unauthenticated',
          err
        );
        return of(req);
      }),
      switchMap((request) =>
        next.handle(request).pipe(
          catchError((err: unknown) => {
            const alreadyRetried = req.headers.get(AUTH_RETRY_HEADER) === '1';
            if (
              alreadyRetried ||
              !(err instanceof HttpErrorResponse) ||
              err.status !== 401
            ) {
              return throwError(() => err);
            }
            return this.forceRefreshEntraAndRetry(req, next, err);
          })
        )
      )
    );
  }

  /**
   * A token was attached but the API answered 401 — the access token is stale
   * (issued before a role/scope change, or a cached token MSAL still considers
   * live). Force a NON-cached refresh via `AuthFacade.getAccessToken(true)`
   * (#31749 — the same forced-acquire the Auth0 branch uses; load-bearing
   * because a plain silent call returns the same token that just 401'd, and
   * routing through the facade reuses the AuthenticationService's scopes and
   * MSAL-init gating) and replay the request exactly once. If the forced
   * refresh itself reports
   * interaction is required OR that there is no signed-in account (the session
   * died between the successful token attach and this 401 — e.g. a sign-out in
   * another tab), the session is dead — escalate to an interactive redirect.
   * Either way the ORIGINAL 401 surfaces if recovery fails, never a loop (the
   * retry header caps this at one attempt).
   */
  private forceRefreshEntraAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    originalErr: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return this.facade.getAccessToken(true).pipe(
      // #32087: a forced refresh is a second acquire and can stall like the
      // first; at the bound the catchError below surfaces the ORIGINAL 401.
      timeout({
        first: TOKEN_ACQUIRE_BOUND_MS,
        with: () => throwError(() => new TokenAcquireError('entra', 'timeout')),
      }),
      take(1),
      switchMap((token) => {
        const retried = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            [AUTH_RETRY_HEADER]: '1',
          },
        });
        return next.handle(retried);
      }),
      catchError((refreshErr: unknown) => {
        // no_account_error here is the mid-session variant of the primary
        // path's fail-loud case (#30531): the account vanished after the
        // token was attached. Escalate to an interactive redirect rather
        // than silently resurfacing the stale 401, which would be a narrower
        // version of the very bug this PR fixes.
        if (
          needsInteractiveRecovery(refreshErr) ||
          isNoAccountError(refreshErr)
        ) {
          this.triggerEntraRedirect();
        }
        return throwError(() => originalErr);
      })
    );
  }

  /**
   * Kick off MSAL's interactive redirect to re-establish the session, guarded
   * so concurrent 401s fire it exactly once (see {@link redirectInFlight}).
   * The redirect navigates the document to Microsoft and back, rebooting the
   * app with a fresh session — the same recovery `MsalGuard` performs on
   * navigation, made available to background API calls that never navigate.
   */
  private triggerEntraRedirect(): void {
    if (this.redirectInFlight) {
      return;
    }
    // Never redirect from inside a hidden iframe. MSAL's silent-renewal iframe
    // loads this SPA at the app-origin redirectUri, so the app can boot and run
    // this interceptor *inside* that iframe; MSAL refuses a redirect there
    // (`block_iframe_reload`), and redirecting the iframe would be futile
    // anyway. Only the top window drives the recovery. Shares the exact
    // predicate the app-bootstrap guard uses, so "don't bootstrap here" and
    // "don't redirect here" can never disagree (review finding #31904).
    if (isInMsalSilentIframe()) {
      return;
    }
    // Latch for the whole page load: attempt the redirect at most once. If it
    // fails to START (e.g. the underlying block_iframe_reload config issue, or
    // interaction already in progress), retrying cannot help and would spin a
    // console-flooding loop as each queued 401 re-enters. The next full page
    // load resets the latch and tries again from a clean state.
    this.redirectInFlight = true;
    this.injector
      .get(MsalService)
      .acquireTokenRedirect({ scopes: this.entraScopes() })
      .subscribe({
        error: (err) => {
          console.error(
            '[auth] Entra interactive re-authentication failed to start; ' +
              'not retrying this page load',
            err
          );
        },
      });
  }

  /**
   * Scopes the interceptor's own MSAL calls (force-refresh + interactive
   * redirect) request. Read from {@link ENTRA_LOGIN_CONFIG} — the SAME injected
   * config `EntraAuthenticationService.getAccessTokenSilently` uses — so the
   * recovery calls and the primary token flow always request identical scopes
   * and cannot drift. `.default` resolves to every statically-consented
   * permission on the app registration (a superset of any explicit scope list),
   * which is what the two apps wiring this interceptor
   * (web-hr-packet, web-software-security) grant. Review finding #31904.
   */
  private entraScopes(): string[] {
    const audience = this.entraConfig?.audience;
    return audience ? [`${audience}/.default`] : [];
  }

  /**
   * Auth0 mirror of {@link triggerEntraRedirect} (#32087): re-establish the
   * session interactively. Shares the {@link redirectInFlight} latch so N
   * concurrent expired-session failures in one tick fire ONE redirect (auth0-
   * spa-js rejects a second interactive call while one is in progress); safe to
   * share because a session is single-issuer. No hidden-iframe guard is needed:
   * auth0-spa-js runs its silent `/authorize` against the Auth0 domain, not the
   * app origin, so this interceptor never runs inside an Auth0 renewal iframe.
   */
  private triggerAuth0Redirect(): void {
    if (this.redirectInFlight) {
      return;
    }
    this.redirectInFlight = true;
    this.injector
      .get(AuthService)
      .loginWithRedirect()
      .subscribe({
        error: (err) => {
          console.error(
            '[auth] Auth0 interactive re-authentication failed to start; ' +
              'not retrying this page load',
            err
          );
        },
      });
  }

  /**
   * Auth0 path (#31536). Delegate to the stock Auth0 `AuthHttpInterceptor`
   * so the existing allowedList and token-attachment semantics stay
   * byte-identical, but recover from an EXPIRED access token, the case a
   * page left open past the token lifetime (overnight) hits: both saves and
   * the in-page Azure DevOps queries 401 with no recovery, forcing a full
   * logout/login.
   *
   * On a 401 (and only a 401), force a NON-cached silent refresh and replay
   * the original request exactly once. `ignoreCache: true` is load-bearing:
   * a plain silent call returns the very token that just 401'd, so the retry
   * would be pointless. The retry header caps recovery at a single attempt,
   * so a genuinely dead session surfaces its original 401 as the normal
   * re-login signal instead of looping. Non-401 failures (5xx, network) pass
   * straight through untouched. Mirrors the proven mobile pattern
   * (`MobileAuth0HttpInterceptor`).
   *
   * **The acquire itself is bounded (#32087).** The stock interceptor calls
   * `next.handle()` only AFTER `getTokenSilently()` resolves, so a silent
   * renewal that never settles left the request unsent and the caller's
   * stream silent (an infinite spinner invisible to telemetry). The delegate
   * runs under {@link boundTokenAcquire}: the timer covers the acquire only
   * (disarmed the moment the request is issued, so slow API responses are
   * untouched) and fails the stream with a classified
   * {@link TokenAcquireError}. A non-HTTP error out of the delegate can only
   * have come from the acquire (network failures arrive as
   * `HttpErrorResponse`), so it is classified the same way: the SDK's own
   * `TimeoutError` maps to `timeout`, `login_required`-class codes to
   * `session-expired`, anything unrecognised passes through untouched.
   *
   * **A classified acquire failure drives interactive re-login (#32087
   * rescope).** An acquire-time failure sends no request, so there is no 401 to
   * act as the re-login signal and a raw {@link TokenAcquireError} is swallowed
   * by the page as a spinner. BOTH kinds (`timeout`, `session-expired`) trigger
   * {@link triggerAuth0Redirect} before the error is re-thrown (a 20s bound
   * elapsing is strong evidence the renewal machinery is broken, not a blip).
   * An UNRECOGNISED error is never redirected on, so no real cause is masked.
   */
  private interceptAuth0(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const auth0Interceptor = this.injector.get(AuthHttpInterceptor);
    return boundTokenAcquire(
      req,
      next,
      (request, handler) => auth0Interceptor.intercept(request, handler),
      TOKEN_ACQUIRE_BOUND_MS,
      () => new TokenAcquireError('auth0', 'timeout')
    ).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse)) {
          const classified = classifyAuth0AcquireFailure(err);
          // #32087 rescope: an acquire-time failure sends no request, so a raw
          // TokenAcquireError has no 401 signal and the page swallows it as a
          // spinner. Route both kinds to a latched interactive re-login; an
          // unrecognised error (not a TokenAcquireError) is left untouched.
          if (classified instanceof TokenAcquireError) {
            this.triggerAuth0Redirect();
          }
          return throwError(() => classified);
        }
        const alreadyRetried = req.headers.get(AUTH_RETRY_HEADER) === '1';
        if (alreadyRetried || err.status !== 401) {
          return throwError(() => err);
        }

        return this.injector
          .get(AuthService)
          .getAccessTokenSilently({ ignoreCache: true })
          .pipe(
            // #32087: the recovery refresh is a second acquire; at the bound
            // the catchError below surfaces the ORIGINAL 401.
            timeout({
              first: TOKEN_ACQUIRE_BOUND_MS,
              with: () =>
                throwError(() => new TokenAcquireError('auth0', 'timeout')),
            }),
            take(1),
            switchMap(() => {
              const retried = req.clone({
                setHeaders: { [AUTH_RETRY_HEADER]: '1' },
              });
              return auth0Interceptor.intercept(retried, next);
            }),
            // The refresh failed, or the retried request also failed: surface
            // the ORIGINAL 401 (the re-login signal), never a second refresh.
            catchError(() => throwError(() => err))
          );
      })
    );
  }
}
